import { createTheme } from '@mui/material/styles';

const inputBg = (mode) =>
  mode === 'dark' ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.96)';

const inputBgHover = (mode) =>
  mode === 'dark' ? 'rgba(15, 23, 42, 0.88)' : '#ffffff';

const outlineBorder = (mode) =>
  mode === 'dark' ? 'rgba(148, 163, 184, 0.22)' : 'rgba(148, 163, 184, 0.44)';

/**
 * MUI theme aligned with app CSS variables (teal accent, warm light / slate dark).
 */
export function createAppMuiTheme(mode) {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: isDark ? '#2dd4bf' : '#0f766e',
        dark: isDark ? '#14b8a6' : '#115e59',
        light: isDark ? '#5eead4' : '#0d9488',
        contrastText: '#ffffff',
      },
      error: {
        main: isDark ? '#f87171' : '#dc2626',
      },
      background: {
        default: isDark ? '#090c10' : '#f6f3ed',
        paper: isDark ? 'rgba(30, 41, 59, 0.96)' : '#ffffff',
      },
      text: {
        primary: isDark ? '#e2e8f0' : '#111827',
        secondary: isDark ? '#94a3b8' : '#64748b',
      },
      divider: isDark ? 'rgba(148, 163, 184, 0.16)' : 'rgba(148, 163, 184, 0.32)',
      action: {
        active: isDark ? '#94a3b8' : '#64748b',
      },
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily:
        "ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    components: {
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          input: {
            paddingTop: '8.5px',
            paddingBottom: '8.5px',
            fontSize: '0.875rem',
          },
          inputSizeSmall: {
            paddingTop: '8.5px',
            paddingBottom: '8.5px',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 12,
            backgroundColor: inputBg(theme.palette.mode),
            transition:
              'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
            '&:hover': {
              backgroundColor: inputBgHover(theme.palette.mode),
            },
            '&.Mui-focused': {
              backgroundColor: inputBgHover(theme.palette.mode),
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: outlineBorder(theme.palette.mode),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
              borderColor: theme.palette.primary.main,
            },
          }),
          notchedOutline: {
            borderWidth: '1px',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiOutlinedInput-root': {
              backgroundColor: `${inputBg(theme.palette.mode)} !important`,
            },
          }),
          paper: ({ theme }) => ({
            marginTop: 6,
            borderRadius: 12,
            border: `1px solid ${outlineBorder(theme.palette.mode)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 20px 48px rgba(0, 0, 0, 0.48)'
                : '0 20px 48px rgba(15, 23, 42, 0.12)',
          }),
          listbox: {
            padding: '6px',
            maxHeight: 320,
          },
          option: {
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: '0.875rem',
          },
        },
      },
      MuiPopper: {
        styleOverrides: {
          root: {
            zIndex: 1400,
          },
        },
      },
    },
  });
}
